import {
  bytesToMB,
  calculateGCD,
  LIMIT_IMAGE_SIZE_MB,
  LIMIT_VIDEO_SIZE_MB,
} from 'utilities/file';
import { t } from 'utilities/lang';

export const UPSELL_CREATIVE_PRICE_PER_VIDEO = 200;

export const sizes = [
  { title: '300x250', subtitle: t('library.creatives.size.medium_rectangle') },
  { title: '300x600', subtitle: t('library.creatives.size.large_skyscraper') },
  { title: '160x600', subtitle: t('library.creatives.size.wide_skyscraper') },
  { title: '728x90', subtitle: t('library.creatives.size.leaderboard') },
  { title: '320x50', subtitle: t('library.creatives.size.mobile_leaderboard') },
  { title: '970x250', subtitle: t('library.creatives.size.billboard') },
];

export const videoProgrammaticAdLengths = [
  {
    videoLength: 6,
    title: t('library.creatives.video_length.06_sec'),
  },
  {
    videoLength: 15,
    title: t('library.creatives.video_length.15_sec'),
  },
  {
    videoLength: 30,
    title: t('library.creatives.video_length.30_sec'),
  },
  {
    videoLength: 60,
    title: t('library.creatives.video_length.60_sec'),
  },
];

export const videoCTVAdLengths = [
  {
    videoLength: 15,
    title: t('library.creatives.video_length.15_sec'),
  },
  {
    videoLength: 30,
    title: t('library.creatives.video_length.30_sec'),
  },
];

export const VIDEO_RATIO_ACCEPT = '16:9';

export const BANNER_OPTIONS_FILTER = sizes.map((el) => ({
  id: el.title,
  name: el.title,
  subTitle: el.subtitle,
}));

export const VIDEO_OPTIONS_FILTER = videoProgrammaticAdLengths.map((el) => ({
  id: `${el.videoLength}-sec`,
  name: el.title.replace(':', ''),
}));

export const CREATIVE_TYPE = {
  BANNER: 'Banner',
  VIDEO: 'Video',
  FULLSET: 'FullSet',
  CTV: 'CTV',
};

export const CREATIVE_TYPE_NAME = {
  [CREATIVE_TYPE.BANNER]: t('library.creatives.type.banner'),
  [CREATIVE_TYPE.VIDEO]: t('library.creatives.type.video'),
  [CREATIVE_TYPE.FULLSET]: t('library.creatives.type.banner_fullset'),
  [CREATIVE_TYPE.CTV]: t('library.creatives.type.video_ctv'),
};

export const CREATIVE_STATUS = {
  APPROVED: 'Approved',
  IN_REVIEW: 'InReview',
  PENDING: 'Pending',
  REJECTED: 'Rejected',
  HOLD: 'Hold'
};

export const CREATIVE_STATUS_NAME = {
  [CREATIVE_STATUS.APPROVED]: t('library.creatives.status.approved'),
  [CREATIVE_STATUS.IN_REVIEW]: t('library.creatives.status.in_review'),
  [CREATIVE_STATUS.PENDING]: t('library.creatives.status.pending'),
  [CREATIVE_STATUS.REJECTED]: t('library.creatives.status.rejected'),
  [CREATIVE_STATUS.HOLD]: t('library.creatives.status.hold'),
}

export const IMPORT_TYPE = {
  LINK: 'FromUrl',
  VIDEO: 'FromFile',
  LIBRARY: 'FromLibrary',
};

export const IMPORT_TYPE_DEFAULT = {
  type: '',
  link: '',
  duration: 0,
  file: null,
  valid: false,
  library: null,
};

export const fileNotMeetSpec = t('upload_creatives.error.specs');

export const isValidVideoDuration = (duration) => {
  return videoProgrammaticAdLengths.some(
    (el) => duration >= el.videoLength - 1 && duration <= el.videoLength + 1
  );
};

export const isValidCTVVideoDuration = (duration) => {
  return videoCTVAdLengths.some(
    (el) => duration >= el.videoLength - 1 && duration <= el.videoLength + 1
  );
};

export const isValidVideo = (file) => {
  return (
    bytesToMB(file.size) <= LIMIT_VIDEO_SIZE_MB &&
    isValidVideoDuration(file.duration) &&
    file.ratio === VIDEO_RATIO_ACCEPT
  );
};

export const isValidImage = (file) => {
  return (
    bytesToMB(file.size) <= LIMIT_IMAGE_SIZE_MB &&
    sizes.some((el) => el.title === `${file.width}x${file.height}`)
  );
};

export const getSpecTemplateDesc = (type, templateSpec) => {
  if (type === CREATIVE_TYPE.FULLSET) {
    return `${CREATIVE_TYPE_NAME[type]} (${sizes
      .map((e) => e.title)
      .join(', ')})`;
  }
  return [CREATIVE_TYPE_NAME[type], templateSpec].filter((e) => !!e).join(' - ');
};

export const isValidProgrammaticVideo = (file, type) => {
  let errors = [];
  const genaralInfor = file?.media?.track.find((d) => d['@type'] === 'General');
  const videoInfor = file?.media?.track.find((d) => d['@type'] === 'Video');
  const audioInfor = file?.media?.track.find((d) => d['@type'] === 'Audio');

  // All videos should be provided in .mp4 format.
  if (type !== 'video/mp4') {
    errors.push(t('library.creatives.error.video_format_mp4'));
  }

  // All videos must be under the 100MB max file size per creative unit.
  if (bytesToMB(genaralInfor.FileSize) > LIMIT_VIDEO_SIZE_MB) {
    errors.push(t('library.creatives.error.video_size_100mb'));
  }

  // All videos must be in 16:9 aspect ratio (no square videos or vertical videos).
  if (videoInfor?.Width && videoInfor?.Height) {
    const ratio = calculateGCD(
      Number(videoInfor?.Width),
      Number(videoInfor?.Height)
    );
    const aspectRatio = `${videoInfor?.Width / ratio}:${
      videoInfor?.Height / ratio
    }`;
    if (aspectRatio !== VIDEO_RATIO_ACCEPT) {
      errors.push(t('library.creatives.error.video_aspect_ratio'));
    }
  } else errors.push(t('library.creatives.error.video_aspect_ratio'));

  // All Programmatic Video (Pre–roll / Mid-roll) videos must adhere exactly to one of four standard ad lengths: 06-sec / 15-sec / 30-sec / 60-sec
  if (!videoInfor?.Duration || !isValidVideoDuration(videoInfor?.Duration)) {
    errors.push(t('library.creatives.error.video_standard_lengths'));
  }

  return errors;
};

export const isValidCTVVideo = (file, type) => {
  let errors = [];
  const genaralInfor = file?.media?.track.find((d) => d['@type'] === 'General');
  const videoInfor = file?.media?.track.find((d) => d['@type'] === 'Video');
  const audioInfor = file?.media?.track.find((d) => d['@type'] === 'Audio');
  /* All CTV/OTT videos must also meet the following technical requirements: */
  // Dimensions (px): 1280x720 (minimum) // 1920x1080 (maximum)
  if (
    !(
      videoInfor?.Width >= 1260 &&
      videoInfor?.Width <= 1920 &&
      videoInfor?.Height >= 720 &&
      videoInfor?.Height <= 1080
    )
  ) {
    errors.push(t('library.creatives.error.ctv_dimensions'));
  }
  // Bit Rate: 10-40 mbps
  const bitRateMbps = Number(videoInfor?.BitRate) / 1000000;
  if (bitRateMbps < 10 || bitRateMbps > 40) {
    errors.push(t('library.creatives.error.ctv_bitrate'));
  }
  // Bit Depth: 8 or 16
  const bitDepth = Number(videoInfor.BitDepth);
  if (![8, 16].includes(bitDepth)) {
    errors.push(t('library.creatives.error.ctv_bitdepth'));
  }
  // Codec: H.264 or Apple ProRes 422 HQ
  if (!['avc1', 'apcn'].includes(videoInfor.CodecID)) {
    errors.push(t('library.creatives.error.ctv_codec'));
  }
  // Frame Rate: 23.98 or 25 or 29.97
  const frameRate = parseFloat(videoInfor.FrameRate).toFixed(2);
  if (!['23.98', '25.00', '29.97'].includes(frameRate.toString())) {
    errors.push(t('library.creatives.error.ctv_framerate'));
  }

  // 2-Channel Stereo
  if (audioInfor?.Channels !== '2') {
    errors.push(t('library.creatives.error.ctv_audio_channels'));
  }
  // PCM or AAC
  if (!['PCM', 'AAC'].includes(audioInfor?.Format)) {
    errors.push(t('library.creatives.error.ctv_audio_format'));
  }
  // 16 or 24 Bit Depth, format AAC is lossy audio => skip check bit depth
  const bitDepthAudio = Number(audioInfor?.BitDepth);
  if (audioInfor?.Format === 'PCM' && ![16, 24].includes(bitDepthAudio)) {
    errors.push(t('library.creatives.error.ctv_audio_bitdepth'));
  }

  // 192-256 kbps Bit Rate
  const bitRateAudioKbps = Number(audioInfor?.BitRate) / 1000;
  if (!bitRateAudioKbps || bitRateAudioKbps < 192 || bitRateAudioKbps > 256) {
    errors.push(t('library.creatives.error.ctv_audio_bitrate'));
  }

  // All videos should be provided in .mp4 format.
  if (type !== 'video/mp4') {
    errors.push(t('library.creatives.error.video_format_mp4'));
  }

  if (bytesToMB(genaralInfor.FileSize) > 250) {
    errors.push(t('library.creatives.error.ctv_video_size_250mb'));
  }

  // All videos must be in 16:9 aspect ratio (no square videos or vertical videos).
  const ratio = calculateGCD(
    Number(videoInfor?.Width),
    Number(videoInfor?.Height)
  );
  const aspectRatio = `${videoInfor?.Width / ratio}:${
    videoInfor?.Height / ratio
  }`;
  if (aspectRatio !== VIDEO_RATIO_ACCEPT) {
    errors.push(t('library.creatives.error.video_aspect_ratio'));
  }

  // All Programmatic Video (Pre–roll / Mid-roll) videos must adhere exactly to one of two standard ad lengths: 15-sec / 30-sec
  const duration = Math.floor(videoInfor?.Duration);
  if (!isValidCTVVideoDuration(duration)) {
    errors.push(t('library.creatives.error.ctv_standard_lengths'));
  }

  return errors;
};

export const CREATIVE_TYPE_OPTIONS = [
  {
    id: undefined,
    name: t('common.all'),
  },
  { id: 'Banner', name: t('library.creatives.type.banner') },
  { id: 'Video', name: t('library.creatives.type.video') },
  { id: 'CTV', name: t('library.creatives.type.ctv') },
];
